import { useEffect, useState } from "react";
import MovementIcon from "../../components/SeasonLeague/MovementIcon";

const SeasonLeague = () => {
  const backendURL = process.env.REACT_APP_API_URL;

  const [gameweek, setGameweek] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch current gameweek
  useEffect(() => {
    async function fetchGameweek() {
      try {
        const res = await fetch(`${backendURL}/current-gameweek`);

        if (!res.ok) {
          throw new Error("Failed to fetch gameweek");
        }

        const data = await res.json();
        setGameweek(data.currentGameweek ?? null);
      } catch (err) {
        console.error("Error fetching gameweek:", err);
      }
    }

    fetchGameweek();
  }, [backendURL]);

  // Fetch season league standings
  useEffect(() => {
    async function fetchStandings() {
      try {
        const res = await fetch(
          `${backendURL}/api/season-league/standings`
        );

        if (!res.ok) {
          throw new Error("Failed to fetch standings");
        }

        const data = await res.json();
        setPlayers(data.players ?? []);
      } catch (err) {
        console.error("Error fetching season league standings:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchStandings();
  }, [backendURL]);

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-4xl">

        {/* Header */}
        <div className="mb-5">
          <h1 className="text-[20px] font-semibold leading-tight text-gray-900 sm:text-[22px]">
            FPL365 Season League
          </h1>

          <p className="mt-1 text-[16px] font-normal text-gray-900 sm:text-[18px]">
            Gameweek {gameweek ?? "..."}
          </p>

          <p className="mt-1 text-[13px] text-gray-500">
            Standings after Gameweek {gameweek ?? "..."}
          </p>

          <p className="mt-2 text-[12px] text-red-500">
            Note: Click a player's name to view their FPL team.
          </p>
        </div>
        {/* Loading */}
        {loading ? (
          <div className="rounded-lg border border-gray-200 bg-white py-8 text-center text-[14px] text-gray-400">
            Loading standings...
          </div>
        ) : players.length === 0 ? (
          /* Empty */
          <div className="rounded-lg border border-gray-200 bg-white py-8 text-center text-[14px] text-gray-400">
            No players found.
          </div>
        ) : (
          <>
            {/* =========================
                MOBILE
            ========================== */}
            <div className="flex flex-col gap-2 sm:hidden">
              {players.map((p, i) => (
                <div
                  key={p.fplId ?? i}
                  className="flex min-h-[58px] items-center justify-between rounded-lg border border-gray-200 bg-white px-3"
                >
                  {/* Position + Player */}
                  <div className="flex min-w-0 items-center gap-2">
                    <div className="flex w-[34px] shrink-0 items-center justify-start gap-1">
                      <MovementIcon movement="same" />

                      <span className="text-[14px] font-semibold text-gray-900">
                        {i + 1}
                      </span>
                    </div>

                    <span className="truncate text-[14px] font-medium text-gray-900">
                        <a
                          href={`https://fantasy.premierleague.com/en/entry/${p.fplId}/event/${gameweek}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="truncate text-[14px] font-medium text-gray-900 hover:text-gray-600 no-underline"
                        >
                          {p.player}
                        </a>
                    </span>
                  </div>

                  {/* Points */}
                  <div className="flex shrink-0 items-center gap-4 pl-3">
                    <div className="text-right">
                      <div className="text-[9px] font-medium uppercase tracking-wide text-gray-400">
                        GW {gameweek ?? "-"}
                      </div>

                      <div className="mt-0.5 text-[14px] font-medium text-gray-700">
                        {p.gw}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-[9px] font-medium uppercase tracking-wide text-gray-400">
                        Total
                      </div>

                      <div className="mt-0.5 text-[14px] font-bold text-gray-900">
                        {p.total}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* =========================
                DESKTOP / TABLET
            ========================== */}
            <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white sm:block">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px] border-collapse">
                  <thead>
                    <tr className="h-[44px] bg-gray-50">
                      <th className="w-[92px] px-3 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Pos
                      </th>

                      <th className="px-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Player
                      </th>

                      <th className="w-[120px] px-4 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        GW {gameweek ?? "-"}
                      </th>

                      <th className="w-[150px] px-4 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Total Points
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {players.map((p, i) => (
                      <tr
                        key={p.fplId ?? i}
                        className="h-[58px] border-t border-gray-100 transition-colors hover:bg-gray-50"
                      >
                        {/* Position */}
                        <td className="w-[92px] px-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <MovementIcon movement="same" />

                            <span className="text-[14px] font-semibold text-gray-900">
                              {i + 1}
                            </span>
                          </div>
                        </td>

                        {/* Player */}
                        <td className="px-4 text-left text-[14px] font-medium text-gray-900">
                            <a
                              href={`https://fantasy.premierleague.com/en/entry/${p.fplId}/event/${gameweek}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-900 hover:text-gray-600 no-underline"
                            >
                              {p.player}
                            </a>
                        </td>

                        {/* Gameweek Points */}
                        <td className="w-[120px] px-4 text-right text-[14px] font-medium text-gray-700">
                          {p.gw}
                        </td>

                        {/* Total Points */}
                        <td className="w-[150px] px-4 text-right text-[14px] font-bold text-gray-900">
                          {p.total}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SeasonLeague;